import {
  EnvironmentType,
  useEnvironments,
} from "@app-context/environments/context";
import { useRefCallback } from "@enfusion-ui/hooks";
import {
  Button,
  ControlledSelect,
  FormSectionAccordion,
  Modal,
} from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CustomEnvInputs } from "./CustomEnvInputs";
import { DefaultEnvInputs, SingleRowContainer } from "./DefaultEnvInputs";

const FormContainer = styled.form`
  display: block;
  padding: var(--spacing);
`;

const FormContent = styled.div`
  > :not(:last-child) {
    margin-bottom: var(--spacing-l);
  }
  > :last-child {
    margin-bottom: var(--spacing-xl);
  }
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-xl);
`;

export type EnvironmentConfigProps = {
  open: boolean;
  onClose: VoidFunction;
};

type EnvConfigValues = {
  authEnvironment: EnvironmentType;
  customEnvironments: EnvironmentType[];
};

export const EnvironmentConfigModal: React.FC<EnvironmentConfigProps> = ({
  open,
  onClose,
}) => {
  const {
    customEnvironments,
    authEnvironment,
    environments,
    setCustomEnvironments,
    setAuthEnvironment,
  } = useEnvironments();

  const formMethods = useForm<EnvConfigValues>({
    defaultValues: { authEnvironment, customEnvironments },
  });

  React.useEffect(() => {
    formMethods.reset({ authEnvironment, customEnvironments });
  }, [authEnvironment, customEnvironments]);

  const handleApply = useRefCallback(
    (data: EnvConfigValues) => {
      setAuthEnvironment(data.authEnvironment);
      setCustomEnvironments(data.customEnvironments);
      onClose();
    },
    [onClose, setCustomEnvironments, setAuthEnvironment]
  );

  const handleCancel = useRefCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      allowDismissal
      size="medium"
      title="Environment Configurations"
      content={
        <>
          <FormProvider {...formMethods}>
            <FormContainer onSubmit={formMethods.handleSubmit(handleApply)}>
              <FormContent>
                <FormSectionAccordion title="Auth Environment" defaultOpen>
                  <SingleRowContainer count={1}>
                    <ControlledSelect
                      name="authEnvironment"
                      options={environments.map((i) => ({
                        label: i.label,
                        value: i,
                      }))}
                      minWidth={150}
                    />
                  </SingleRowContainer>
                </FormSectionAccordion>

                <DefaultEnvInputs />

                <FormSectionAccordion title="Custom Environments">
                  <CustomEnvInputs />
                </FormSectionAccordion>
              </FormContent>

              <FooterContainer>
                <Button type="submit" primary>
                  Apply
                </Button>
                <Button
                  type="button"
                  theme="secondary"
                  onClick={() => formMethods.reset()}
                >
                  Revert
                </Button>
                <Button type="button" theme="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </FooterContainer>
            </FormContainer>
          </FormProvider>
        </>
      }
    />
  );
};
